/**
 * ChannelContext - Manages channel state for the ChannelsPage.
 *
 * Scoped to ChannelsPage only (not global) to avoid overhead on non-channel pages.
 * Handles WebSocket events for real-time message delivery, typing indicators, etc.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { websocketService } from '../services/websocket';
import { useAuth } from './AuthContext';

const ChannelContext = createContext(null);

export function ChannelProvider({ teamId, children }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [openThreadId, setOpenThreadId] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimeoutsRef = useRef({});

  // Clear typing user after 4 seconds
  const clearTypingTimeout = useCallback((channelId, userId) => {
    const key = `${channelId}:${userId}`;
    if (typingTimeoutsRef.current[key]) {
      clearTimeout(typingTimeoutsRef.current[key]);
    }
    typingTimeoutsRef.current[key] = setTimeout(() => {
      setTypingUsers(prev => {
        const channelTyping = { ...(prev[channelId] || {}) };
        delete channelTyping[userId];
        if (Object.keys(channelTyping).length === 0) {
          const next = { ...prev };
          delete next[channelId];
          return next;
        }
        return { ...prev, [channelId]: channelTyping };
      });
      delete typingTimeoutsRef.current[key];
    }, 4000);
  }, []);

  // WebSocket event handlers
  useEffect(() => {
    const handleNewMessage = (data) => {
      const { message, channel_id, team_id } = data;
      if (team_id !== teamId) return;

      if (channel_id === activeChannelId) {
        queryClient.setQueryData(['messages', channel_id], (old) => {
          if (!old) return old;
          const pages = old.pages ? [...old.pages] : [];

          // Check if message already exists (from optimistic send or duplicate WebSocket)
          const exists = pages.some(page =>
            (page.messages || []).some(m => m.id === message.id)
          );
          if (exists) return old;

          // Check for optimistic temp message from same author with same content and replace it
          let replaced = false;
          const updatedPages = pages.map((page, pageIdx) => {
            if (pageIdx !== pages.length - 1) return page;
            return {
              ...page,
              messages: (page.messages || []).map(m => {
                if (m._optimistic && m.author_id === message.author_id && m.content === message.content) {
                  replaced = true;
                  return message;
                }
                return m;
              }),
            };
          });

          if (replaced) return { ...old, pages: updatedPages };

          // New message from another user: append to last page
          if (pages.length > 0) {
            const lastPage = { ...pages[pages.length - 1] };
            lastPage.messages = [...(lastPage.messages || []), message];
            pages[pages.length - 1] = lastPage;
          }
          return { ...old, pages };
        });
      }

      // Always invalidate unread counts
      queryClient.invalidateQueries({ queryKey: ['unread-counts', teamId] });
      queryClient.invalidateQueries({ queryKey: ['channels', teamId] });
    };

    const handleMessageUpdated = (data) => {
      const { message, channel_id } = data;
      queryClient.setQueryData(['messages', channel_id], (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            messages: (page.messages || []).map(m =>
              m.id === message.id ? { ...m, ...message } : m
            ),
          })),
        };
      });
    };

    const handleMessageDeleted = (data) => {
      const { message_id, channel_id } = data;
      queryClient.setQueryData(['messages', channel_id], (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            messages: (page.messages || []).filter(m => m.id !== message_id),
          })),
        };
      });
    };

    const handleTyping = (data) => {
      const { user_id, user_name, channel_id } = data;
      setTypingUsers(prev => ({
        ...prev,
        [channel_id]: {
          ...(prev[channel_id] || {}),
          [user_id]: user_name,
        },
      }));
      clearTypingTimeout(channel_id, user_id);
    };

    const handleReactionUpdated = (data) => {
      const { message_id, channel_id, emoji, user_id, user_name, action } = data;
      // Skip if this is from the current user — already handled by optimistic update
      if (user?.id === user_id) return;
      queryClient.setQueryData(['messages', channel_id], (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            messages: (page.messages || []).map(m => {
              if (m.id !== message_id) return m;
              let reactions = [...(m.reactions || [])];
              const idx = reactions.findIndex(r => r.emoji === emoji);
              if (action === 'added') {
                if (idx >= 0) {
                  reactions[idx] = {
                    ...reactions[idx],
                    count: reactions[idx].count + 1,
                    users: [...reactions[idx].users, { id: user_id, name: user_name }],
                  };
                } else {
                  reactions.push({ emoji, count: 1, users: [{ id: user_id, name: user_name }] });
                }
              } else if (idx >= 0) {
                reactions[idx] = {
                  ...reactions[idx],
                  count: Math.max(0, reactions[idx].count - 1),
                  users: reactions[idx].users.filter(u => u.id !== user_id),
                };
                if (reactions[idx].count === 0) {
                  reactions = reactions.filter(r => r.emoji !== emoji);
                }
              }
              return { ...m, reactions };
            }),
          })),
        };
      });
    };

    const handleThreadUpdated = (data) => {
      const { parent_message_id, reply_count, last_reply_at, channel_id } = data;
      queryClient.setQueryData(['messages', channel_id], (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            messages: (page.messages || []).map(m =>
              m.id === parent_message_id
                ? { ...m, reply_count, last_reply_at }
                : m
            ),
          })),
        };
      });
    };

    websocketService.on('channel_message_new', handleNewMessage);
    websocketService.on('channel_message_updated', handleMessageUpdated);
    websocketService.on('channel_message_deleted', handleMessageDeleted);
    websocketService.on('channel_typing', handleTyping);
    websocketService.on('channel_reaction_updated', handleReactionUpdated);
    websocketService.on('channel_thread_updated', handleThreadUpdated);

    return () => {
      websocketService.off('channel_message_new', handleNewMessage);
      websocketService.off('channel_message_updated', handleMessageUpdated);
      websocketService.off('channel_message_deleted', handleMessageDeleted);
      websocketService.off('channel_typing', handleTyping);
      websocketService.off('channel_reaction_updated', handleReactionUpdated);
      websocketService.off('channel_thread_updated', handleThreadUpdated);

      // Clear all typing timeouts
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      typingTimeoutsRef.current = {};
    };
  }, [teamId, activeChannelId, queryClient, clearTypingTimeout, user]);

  // Send typing indicator (debounced on frontend, 1 event per 3s)
  const lastTypingSentRef = useRef(0);
  const sendTyping = useCallback((channelId) => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < 3000) return;
    lastTypingSentRef.current = now;
    websocketService.send({ type: 'typing', channel_id: channelId });
  }, []);

  const value = useMemo(
    () => ({
      activeChannelId,
      setActiveChannelId,
      openThreadId,
      setOpenThreadId,
      typingUsers,
      sendTyping,
      teamId,
    }),
    [activeChannelId, openThreadId, typingUsers, sendTyping, teamId],
  );

  return (
    <ChannelContext.Provider value={value}>
      {children}
    </ChannelContext.Provider>
  );
}

export function useChannels() {
  const context = useContext(ChannelContext);
  if (!context) {
    throw new Error('useChannels must be used within a ChannelProvider');
  }
  return context;
}
